import React, { useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import { Botao, PageHeader } from '../../../ui';
import StepperAdesao from '../../../components/adesao/StepperAdesao';
import FormularioVeiculo, {
  DadosVeiculo,
  HandleFormularioVeiculo,
  VeiculoNoSga,
  valorBaseDoVeiculo,
} from '../../../components/carro/FormularioVeiculo';

import ModalImportacaoSga from '../../../components/modal/importacao-sga/ModalImportacaoSga';
import AvisoDivergenciaSga from '../../../components/divergencia-sga/AvisoDivergenciaSga';

import { RootState } from '../../../store/store';
import { addCarroData } from '../../../store/associadoSlice';
import { setCodigoData } from '../../../store/codigoSlice';

import { fetchCarroPorPlaca } from '../../../api/carro/carroApi';
import { reconciliarVeiculo } from '../../../api/sga-reconciliacao/sgaReconciliacaoApi';
import { DivergenciaSga } from '../../../api/sga-divergencia/sgaDivergenciaApi';
import {
  importarVeiculoSga,
  previewVeiculoSga,
  PreviewVeiculoSga,
} from '../../../api/sga-importacao/sgaImportacaoApi';
import {
  AcaoSincronizacaoSga,
  mensagemDeErroSincronizacao,
  sincronizarVeiculoComSga,
} from '../../../utils/sincronizacaoSga';

import { numeroParaMoeda } from '../../../utils/moeda';
import { extrairMensagemErro } from '../../../utils/apiError';

import { DOCUMENTO_EDIT } from '../../documento/routes';

/**
 * `/carro/substituir` — **etapa 2 da Substituição de Titular** (at-59, C2).
 *
 * ⚠️ **O nome do arquivo mente**: não é "substituir veículo" (isso é
 * `/carro/edit`). Aqui o VEÍCULO é o mesmo e quem muda é o titular, e a tela é
 * aberta pela opção **"Substituição de Titular"** do menu global (`plan-c` §1.4).
 *
 * É a mais complexa da família, e o que ela tem a mais que `/carro` são **três
 * mecanismos do SGA**, todos preservados desta reescrita:
 *
 *   1. **Busca por placa** (`fetchCarroPorPlaca`) que pré-carrega o formulário
 *      inteiro — é o caminho normal desta tela.
 *   2. **Importação sob demanda** (at-32, item 30): placa que não existe no SAAC
 *      pode existir no SGA. Sem isto, substituir o titular de um veículo nascido
 *      lá era **impossível**.
 *   3. **Reconciliação on-access** (at-32, Plano B): esta é a tela mais crítica —
 *      operar sobre um veículo que já foi trocado direto no SGA é o pior caso. O
 *      aviso é informativo e **nunca bloqueia** a substituição.
 *
 * ## O que a reescrita mudou de COMPORTAMENTO
 *
 *   1. **A tela ganhou "Voltar"** — não tinha nenhum.
 *   2. **Fim dos 3 segundos** de espera antes de navegar.
 *   3. **Erro aparece junto do campo**, em vez do "Salvar" desabilitado e mudo.
 *   4. 🐛 **"Selecionar Categoria" voltou a funcionar depois de carregar a placa.**
 *      As telas antigas guardavam um `codigoTipo` paralelo que só a escolha manual
 *      do tipo alimentava — então, com o veículo carregado pela placa, o tipo
 *      aparecia preenchido e o botão respondia *"Selecione um Tipo de Veículo
 *      antes de continuar"*. O `FormularioVeiculo` deriva do próprio campo.
 */
const CarroSubstituirPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const voltar = useVoltar();

  const associado = useSelector((state: RootState) => state.associado);

  /** Empurra para dentro do formulário o veículo carregado — ver o handle lá. */
  const formulario = useRef<HandleFormularioVeiculo>(null);

  const [buscandoPlaca, setBuscandoPlaca] = useState(false);

  /**
   * at-30: o veículo carregado que já está no SGA. Vira a lista que o formulário
   * usa para travar o valor protegido — enquanto a tela descrever o mesmo veículo
   * (placa + `codigoFipe` + bike), o valor é o de lá.
   */
  const [veiculoNoSga, setVeiculoNoSga] = useState<VeiculoNoSga | null>(null);

  /**
   * Importação sob demanda (at-32): a substituição partia SÓ do banco local, então
   * veículo que nasceu no SGA era impossível de substituir (item 30).
   */
  const [placaSemRegistroLocal, setPlacaSemRegistroLocal] = useState<string | null>(null);
  const [previewSga, setPreviewSga] = useState<PreviewVeiculoSga | null>(null);
  const [modalImportacaoAberto, setModalImportacaoAberto] = useState(false);
  const [buscandoNoSga, setBuscandoNoSga] = useState(false);
  const [importandoDoSga, setImportandoDoSga] = useState(false);

  // Reconciliação on-access (at-32, Plano B).
  const [divergenciasSga, setDivergenciasSga] = useState<DivergenciaSga[]>([]);
  const [sincronizandoSga, setSincronizandoSga] = useState(false);
  const [carroIdReconciliado, setCarroIdReconciliado] = useState('');

  const enderecoDoTitular = {
    rua: associado.rua || '',
    numero: associado.numero || '',
    complemento: associado.complemento || '',
    bairro: associado.bairro || '',
    cidade: associado.cidade || '',
    estado: associado.estado || '',
    cep: associado.cep || '',
  };

  /** Carrega o veículo pela placa e pré-carrega o formulário inteiro. */
  const buscarPorPlaca = async (placa: string) => {
    if (!placa.trim()) {
      toast.warning('Informe a placa antes de buscar.');
      return null;
    }

    setBuscandoPlaca(true);
    try {
      const res = await fetchCarroPorPlaca(placa);
      if (!res) return null;

      // Achou localmente: some a oferta de importar do SGA.
      setPlacaSemRegistroLocal(null);

      const bike = (res as any).bikeEletrica ?? null;

      setVeiculoNoSga(
        (res as any).addedToSga === true && res.placa
          ? {
              placa: res.placa,
              codigoFipe: res.codigoFipe || '',
              bikeEletricaId: (res as any).bikeEletricaId || '',
              /*
               * ⚠️ **Sem `valorProtegido` de propósito.** Quem repõe o valor no
               * campo travado aqui é o preenchimento abaixo, com o mesmo
               * `numeroParaMoeda(... || null)` de sempre. Passá-lo aqui acionaria
               * também o efeito de reposição do formulário, que trata `0`
               * diferente (`"0,00"` em vez de vazio) — divergência sem ganho.
               */
            }
          : null,
      );

      formulario.current?.preencher({
        placa: res.placa || '',
        anoModelo:
          typeof res.anoModelo === 'number' ? res.anoModelo : parseInt(res.anoModelo, 10) || 0,
        anoFabricacao:
          typeof res.anoFabricacao === 'number'
            ? res.anoFabricacao
            : parseInt(res.anoFabricacao, 10) || 0,
        /*
         * `objVeiculo` é null para veículo sem FIPE (bike, ou código sem match no
         * espelho) — o acesso sem `?.` derrubava a tela inteira nesses casos.
         * Bicicleta (at-46): a marca vem do catálogo, com o prefixo que o
         * `ModalMarcas` usa para marcas de bike.
         */
        marcaId: bike ? `BIKE:${bike.marca?.id ?? ''}` : res.objVeiculo?.marcaId || '',
        marcaNome: bike?.marca?.nome || res.objVeiculo?.brand || '',
        bikeEletricaId: (res as any).bikeEletricaId || '',
        bikeMarcaId: bike?.marca?.id || '',
        modelo: res.modelo || '',
        codigoFipe: res.codigoFipe || '',
        chassi: res.chassi || '',
        renavam: res.renavam || '',
        corId: res.cor || '',
        corNome: res.objCor?.descricao || '',
        combustivelId: res.combustivel || '',
        combustivelNome: res.objCombustivel?.descricao || '',
        cambioId: res.cambio || '',
        tipoVeiculoId: res.tipoVeiculo || '',
        tipoVeiculoNome: res.objTipoVeiculo?.descricao_tipo || '',
        categoriaId: res.categoria || '',
        categoriaNome: res.objCategoria?.descricao_categoria || '',
        cooperativaId: res.cooperativa || '',
        cooperativaNome: res.cooperativaObj?.nome || '',
        cotaId: res.cota || '',
        cotaDescricao: res.objCota?.descricao_cota || '',
        vencimentoDesejado:
          typeof res.vencimentoDesejado === 'number'
            ? res.vencimentoDesejado
            : parseInt(res.vencimentoDesejado, 10) || 0,
        /*
         * at-30: Float do banco precisa virar string MASCARADA — os parsers
         * downstream (`ModalCotas`, `documentoEdit`) assumem 2 casas, e "80668"
         * cru viraria 806,68 (faixa de cota e gravação 100× menores).
         */
        valorFipe: numeroParaMoeda(Number(res.valorFipe) || null),
        valorProtegido: numeroParaMoeda(Number(res.valorProtegido) || null),
        endereco: enderecoDoTitular,
      });

      /*
       * Reconciliação fire-and-forget: a tela não espera o SGA para funcionar. Com
       * ele fora do ar a promise resolve com `verificado: false` e simplesmente
       * não há aviso — a função de API nunca lança.
       */
      if ((res as any).id) {
        setDivergenciasSga([]);
        setCarroIdReconciliado(String((res as any).id));
        void reconciliarVeiculo((res as any).id).then((resultado) =>
          setDivergenciasSga(resultado.divergencias),
        );
      }

      return res;
    } catch (erro) {
      const status = (erro as any)?.response?.status;

      /*
       * 404 aqui NÃO é erro: significa que o veículo pode existir no SGA e nunca
       * ter passado pelo SAAC — é exatamente o caso que a at-32 destrava.
       */
      if (status === 404) {
        setPlacaSemRegistroLocal(placa.replace(/[^a-zA-Z0-9]/g, '').toUpperCase());
        toast.info(
          'Veículo não encontrado no SAAC. Se ele foi cadastrado direto no SGA, use "Buscar no SGA".',
        );
        return null;
      }

      setPlacaSemRegistroLocal(null);
      toast.error(extrairMensagemErro(erro, 'Erro ao buscar dados do carro pela placa.'));
      return null;
    } finally {
      setBuscandoPlaca(false);
    }
  };

  /** Consulta a placa no SGA e abre a confirmação de importação (at-32). */
  const buscarNoSga = async (placa: string) => {
    const limpa = placa.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

    if (limpa.length !== 7) {
      toast.warning('Informe a placa completa (7 caracteres) para buscar no SGA.');
      return;
    }

    setBuscandoNoSga(true);
    setPreviewSga(null);
    setModalImportacaoAberto(true);

    try {
      setPreviewSga(await previewVeiculoSga(limpa));
    } catch (erro) {
      const status = (erro as { status?: number | null })?.status ?? null;
      setModalImportacaoAberto(false);

      if (status === 404) {
        toast.info('Nenhum veículo com esta placa foi encontrado no SGA.');
        return;
      }

      toast.error(extrairMensagemErro(erro, 'Não foi possível consultar o veículo no SGA.'));
    } finally {
      setBuscandoNoSga(false);
    }
  };

  /**
   * Importa e, na sequência, carrega o veículo pelo caminho NORMAL — daí em diante
   * a substituição segue igual à de um veículo nascido no SAAC.
   */
  const confirmarImportacao = async () => {
    if (!previewSga) return;

    setImportandoDoSga(true);
    try {
      const resultado = await importarVeiculoSga(previewSga.placa);
      toast.success(resultado.mensagem);

      setModalImportacaoAberto(false);
      setPreviewSga(null);
      setPlacaSemRegistroLocal(null);

      await buscarPorPlaca(previewSga.placa);
    } catch (erro) {
      toast.error(extrairMensagemErro(erro, 'Não foi possível importar o veículo do SGA.'));
    } finally {
      setImportandoDoSga(false);
    }
  };

  /**
   * Os dois sentidos do espelho (at-32) — a regra vive em `utils/sincronizacaoSga.ts`
   * desde a at-47, porque esta função era **idêntica** à de `/vistoria/captura` e o
   * defeito corrigido lá (B8) estava nas duas cópias.
   */
  const sincronizarComSga = async (acao: AcaoSincronizacaoSga) => {
    if (!carroIdReconciliado) return;

    setSincronizandoSga(true);
    try {
      setDivergenciasSga(
        await sincronizarVeiculoComSga(acao, carroIdReconciliado, divergenciasSga),
      );
    } catch (erro) {
      toast.error(mensagemDeErroSincronizacao(erro));
    } finally {
      setSincronizandoSga(false);
    }
  };

  const salvar = (dados: DadosVeiculo) => {
    /*
     * ⚠️ As três chaves de substituição vão como **string vazia**, e não ausentes:
     * é o que estas telas sempre gravaram (o `getAssociado` que as preencheria
     * está comentado desde antes desta branch), e `documentoEdit` as lê por nome.
     * Trocar `''` por `null` mudaria o que chega ao backend.
     */
    dispatch(
      addCarroData({
        ...dados,
        placaSubstituido: '',
        anoModeloSubstituido: '',
        anoFabricacaoSubstituido: '',
      } as any),
    );

    dispatch(
      setCodigoData({
        codigoRegional: associado.regionalId,
        codigoCooperativa: dados.cooperativaId,
        codigoTipoVeiculo: dados.tipoVeiculoId,
        valorFipe: valorBaseDoVeiculo(dados),
      }),
    );

    /*
     * ⚠️ Sem `setTimeout` e sem toast de sucesso: a etapa 3 abrindo **é** o aviso
     * de que deu certo. Mesma decisão das três telas de `/associado*` (at-58).
     */
    navigate(DOCUMENTO_EDIT());
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader titulo="Veículo" sobretitulo="Substituição de Titular" aoVoltar={voltar} />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              <StepperAdesao etapa={2} fluxo={"SUBSTITUICAO_TITULAR"} />
            </div>

            <div className="tw-p-4 md:tw-p-6">
              <FormularioVeiculo
                ref={formulario}
                tituloIdentificacao="Dados do Veículo a Substituir o Titular"
                rotuloAcao="Continuar — Documentos"
                regionalId={associado.regionalId}
                enderecoAssociado={enderecoDoTitular}
                veiculosNoSga={veiculoNoSga ? [veiculoNoSga] : []}
                mensagemValorTravado="Veículo já enviado ao SGA — valor protegido não pode ser alterado. Selecione o novo modelo para substituir."
                dicaIdentificacaoBike="O substituto é uma motocicleta elétrica: não tem placa. O SAAC gera a identificação ao salvar. Para voltar a buscar um veículo pela placa, selecione outra marca."
                aviso={
                  <AvisoDivergenciaSga
                    divergencias={divergenciasSga}
                    processando={sincronizandoSga}
                    onTrazerDoSga={() => void sincronizarComSga('TRAZER')}
                    onEnviarAoSga={() => void sincronizarComSga('ENVIAR')}
                  />
                }
                acaoPlaca={(placa) => (
                  <div className="tw-flex tw-flex-col tw-gap-3">
                    <Botao
                      variante="secundario"
                      onClick={() => void buscarPorPlaca(placa)}
                      carregando={buscandoPlaca}
                      iconeEsquerda={buscandoPlaca ? undefined : 'busca'}
                      className="sm:tw-self-start"
                    >
                      {buscandoPlaca ? 'Buscando...' : 'Buscar Veículo'}
                    </Botao>

                    {/*
                      at-32: veículo que nasceu no SGA não existe no banco local —
                      sem isto a substituição era impossível para ele (item 30).
                    */}
                    {placaSemRegistroLocal && (
                      <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-amber-warn/30 tw-bg-amber-bg tw-px-4 tw-py-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
                        <p className="tw-m-0 tw-text-sm tw-text-amber-deep">
                          A placa <strong>{placaSemRegistroLocal}</strong> não está no SAAC. Se o
                          veículo foi cadastrado direto no SGA, traga-o para continuar a
                          substituição.
                        </p>
                        <Botao
                          variante="secundario"
                          tamanho="sm"
                          onClick={() => void buscarNoSga(placa)}
                          carregando={buscandoNoSga}
                          className="sm:tw-shrink-0"
                        >
                          {buscandoNoSga ? 'Buscando...' : 'Buscar no SGA'}
                        </Botao>
                      </div>
                    )}
                  </div>
                )}
                aoSalvar={salvar}
              />
            </div>
          </div>
        </div>
      </div>

      <ModalImportacaoSga
        aberto={modalImportacaoAberto}
        tipo="VEICULO"
        preview={previewSga}
        carregando={buscandoNoSga}
        importando={importandoDoSga}
        onConfirmar={() => void confirmarImportacao()}
        onCancelar={() => {
          if (importandoDoSga) return;
          setModalImportacaoAberto(false);
          setPreviewSga(null);
        }}
      />
    </ShellSaac>
  );
};

export default CarroSubstituirPage;
